import i18n from '@/i18n';
import { DEFAULT_LOCALE } from '@/config/site';

/**
 * Resolve the active BCP-47 locale for `Intl.*` formatters.
 *
 * `i18n.language` carries the user's chosen UI language, which is what we want
 * for number/date/currency formatting in 99% of cases. We fall back to the
 * tenant's `DEFAULT_LOCALE` when i18next hasn't initialized yet (SSR-style
 * paths, isolated Vitest runs that don't import the i18n bootstrap).
 */
function activeLocale(explicit?: string): string {
  if (explicit) return explicit;
  const fromI18n = i18n?.language;
  return (fromI18n && fromI18n.length > 0) ? fromI18n : DEFAULT_LOCALE;
}

export interface FormatPriceOptions {
  /** Override the active locale (e.g., "es-MX"). */
  locale?: string;
  /** ISO-4217 currency code. Per `site.config.ts`, currency is per-tenant; we
   * accept it as an arg so test fixtures and admin tools can pin it. */
  currency?: string;
  /** Force `Intl.NumberFormat` minimumFractionDigits (defaults: USD = 2). */
  minimumFractionDigits?: number;
  /** Force `Intl.NumberFormat` maximumFractionDigits. */
  maximumFractionDigits?: number;
}

/**
 * Format a price using `Intl.NumberFormat` style:'currency'.
 *
 * `amount` is in major units (dollars, not cents) — this matches existing
 * data shape across products/orders/etc. If a future migration switches to
 * minor units, change in one place.
 */
export function formatPrice(amount: number, opts: FormatPriceOptions = {}): string {
  const locale = activeLocale(opts.locale);
  const currency = opts.currency ?? 'USD';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: opts.minimumFractionDigits,
    maximumFractionDigits: opts.maximumFractionDigits,
  }).format(amount);
}

export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  locale?: string;
}

/**
 * Format a date with `Intl.DateTimeFormat`. Defaults to a long, human style
 * (e.g., "May 1, 2026"). Pass options to override.
 */
export function formatDate(date: string | Date, opts: FormatDateOptions = {}): string {
  const { locale, ...intlOpts } = opts;
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(activeLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...intlOpts,
  }).format(d);
}

/** Compact "May 1, 2026"-style format used in lists / tables. */
export function formatDateShort(date: string | Date, locale?: string): string {
  return formatDate(date, { locale, month: 'short', day: 'numeric', year: 'numeric' });
}

export interface FormatNumberOptions extends Intl.NumberFormatOptions {
  locale?: string;
}

/** Format a plain number (counts, ratings, percentages). */
export function formatNumber(n: number, opts: FormatNumberOptions = {}): string {
  const { locale, ...intlOpts } = opts;
  return new Intl.NumberFormat(activeLocale(locale), intlOpts).format(n);
}
